import { useCallback, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PillTabs } from "@/components/ui/pill-tabs";
import { FilterBar, FilterSearch } from "@/components/ui/filter-bar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableCard } from "@/components/ui/table-card";
import { useSystemLog, useSystemLogs } from "@/hooks/useSystemLogs";
import type { SystemLog, SystemLogLevel, SystemLogStatusValue } from "@/types";

const LEVEL_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "Wszystkie poziomy" },
  { value: "info", label: "Info" },
  { value: "warn", label: "Ostrzeżenie" },
  { value: "error", label: "Błąd" },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "Wszystkie statusy" },
  { value: "success", label: "Sukces" },
  { value: "failure", label: "Błąd" },
  { value: "partial", label: "Częściowy" },
];

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(iso));
}

// Render duration as seconds for readability — most entries are cron jobs
// in the multi-second range. Anything under 500 ms collapses to "<0.5s"
// because the exact value isn't actionable at that scale (and avoids "0.1s"
// rounding noise). Null/undefined stays as "—" so missing data is distinct
// from a tiny measurement.
function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 500) return "<0.5s";
  return `${(ms / 1000).toFixed(1)}s`;
}

function StatusBadge({ status }: { status: SystemLogStatusValue }) {
  const colors: Record<SystemLogStatusValue, string> = {
    success: "bg-emerald-100 text-emerald-800",
    failure: "bg-red-100 text-red-800",
    partial: "bg-amber-100 text-amber-800",
  };
  const labels: Record<SystemLogStatusValue, string> = {
    success: "Sukces",
    failure: "Błąd",
    partial: "Częściowy",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}
    >
      {labels[status]}
    </span>
  );
}

function LevelBadge({ level }: { level: SystemLogLevel }) {
  const colors: Record<SystemLogLevel, string> = {
    info: "bg-slate-100 text-slate-700",
    warn: "bg-amber-100 text-amber-800",
    error: "bg-red-100 text-red-800",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${colors[level]}`}
    >
      {level}
    </span>
  );
}

function SystemLogDetailDialog({ logId, onClose }: { logId: string | null; onClose: () => void }) {
  const open = logId !== null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Szczegóły logu systemu</DialogTitle>
        </DialogHeader>
        {logId && <SystemLogDetailBody logId={logId} />}
      </DialogContent>
    </Dialog>
  );
}

function SystemLogDetailBody({ logId }: { logId: string }) {
  const { data: log, isLoading, error } = useSystemLog(logId);
  if (isLoading) return <p className="text-muted-foreground">Ładowanie...</p>;
  if (error) return <p className="text-destructive">Błąd: {error.message}</p>;
  if (!log) return <p className="text-muted-foreground">Brak danych.</p>;
  return (
    <div className="space-y-3 text-sm">
      <Field label="Czas">{formatDateTime(log.ts)}</Field>
      <Field label="Domena / akcja">
        <span className="font-mono">{log.domain}</span> /{" "}
        <span className="font-mono">{log.action}</span>
      </Field>
      <Field label="Status">
        <StatusBadge status={log.status as SystemLogStatusValue} />
      </Field>
      <Field label="Poziom">
        <LevelBadge level={log.level as SystemLogLevel} />
      </Field>
      <Field label="Wiadomość">{log.message}</Field>
      {log.durationMs != null && (
        <Field label="Czas trwania">{formatDuration(log.durationMs)}</Field>
      )}
      {log.entityType && (
        <Field label="Powiązany podmiot">
          <span className="font-mono">{log.entityType}</span>
          {log.entityId && (
            <span className="font-mono text-muted-foreground"> / {log.entityId}</span>
          )}
        </Field>
      )}
      {log.requestId && (
        <Field label="Request ID">
          <span className="font-mono text-xs">{log.requestId}</span>
        </Field>
      )}
      {log.userId && (
        <Field label="User ID">
          <span className="font-mono text-xs">{log.userId}</span>
        </Field>
      )}
      {log.metadata && (
        <Field label="Metadane">
          <pre className="bg-slate-50 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words">
            {JSON.stringify(log.metadata, null, 2)}
          </pre>
        </Field>
      )}
      <Field label="ID logu">
        <span className="font-mono text-xs text-muted-foreground">{log.id}</span>
      </Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase font-medium text-muted-foreground mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

export default function SystemLogsPage() {
  const [domain, setDomain] = useState<string>("all");
  const [level, setLevel] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(50);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  const resetCursor = useCallback(() => setCursorStack([]), []);
  const handleDomain = (v: string) => {
    setDomain(v);
    resetCursor();
  };
  const handleLevel = (v: string) => {
    setLevel(v);
    resetCursor();
  };
  const handleStatus = (v: string) => {
    setStatus(v);
    resetCursor();
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    resetCursor();
  };
  const handleLimit = (v: string) => {
    setLimit(Number(v));
    resetCursor();
  };

  const cursor = cursorStack.at(-1);
  const { data, isLoading, error } = useSystemLogs({
    domain: domain === "all" ? undefined : domain,
    level: level === "all" ? undefined : (level as SystemLogLevel),
    status: status === "all" ? undefined : (status as SystemLogStatusValue),
    search: search || undefined,
    cursor,
    limit,
  });

  const rows = data?.data ?? [];
  const hasMore = data?.hasMore ?? false;
  const nextCursor = data?.nextCursor ?? null;

  const goNext = () => {
    if (nextCursor) setCursorStack((prev) => [...prev, nextCursor]);
  };
  const goPrev = () => setCursorStack((prev) => prev.slice(0, -1));
  const page = cursorStack.length + 1;

  return (
    <div>
      <PageHeader
        title="Logi systemu"
        description="Zapisy ważnych zdarzeń systemowych: zadania cron, powiadomienia push, agent AI, wzbogacanie. Wpisy starsze niż 30 dni są usuwane automatycznie."
      />

      <FilterBar>
        <PillTabs
          value={domain}
          onChange={handleDomain}
          options={[
            { value: "all", label: "Wszystkie" },
            { value: "cron", label: "Cron" },
            { value: "push", label: "Push" },
            { value: "ai_agent", label: "Agent AI" },
            { value: "enrichment", label: "Wzbogacanie" },
            { value: "scraper", label: "Skrobak" },
          ]}
        />
        <FilterSearch value={search} onChange={handleSearch} placeholder="Szukaj w wiadomości..." />
        <Select value={level} onValueChange={handleLevel}>
          <SelectTrigger className="h-10 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LEVEL_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={handleStatus}>
          <SelectTrigger className="h-10 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterBar>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded bg-slate-100 animate-pulse" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Błąd ładowania logów: {error.message}</p>}
      {!isLoading && rows.length === 0 && !error && (
        <p className="text-center py-12 text-muted-foreground">Brak wpisów logów systemu.</p>
      )}
      {!isLoading && rows.length > 0 && (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Czas</TableHead>
                <TableHead>Domena / akcja</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Poziom</TableHead>
                <TableHead>Wiadomość</TableHead>
                <TableHead className="text-right">Czas trwania</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((log: SystemLog) => (
                <TableRow
                  key={log.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => setOpenId(log.id)}
                >
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDateTime(log.ts)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <div>{log.domain}</div>
                    <div className="text-muted-foreground">{log.action}</div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={log.status as SystemLogStatusValue} />
                  </TableCell>
                  <TableCell>
                    <LevelBadge level={log.level as SystemLogLevel} />
                  </TableCell>
                  <TableCell className="text-sm max-w-md truncate" title={log.message}>
                    {log.message}
                  </TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">
                    {formatDuration(log.durationMs)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
      )}

      {!isLoading && (rows.length > 0 || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Strona {page}</span>
            <Select value={String(limit)} onValueChange={handleLimit}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>na stronę</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!hasMore}>
              Następna
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <SystemLogDetailDialog logId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
